import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { localizedPath } from "../../hooks/useLocalizedNavigate";
import { motion } from "framer-motion";
import { FiCheck, FiInbox, FiMonitor, FiArrowRight } from "react-icons/fi";
import { useNotifications } from "../../hooks/useNotifications";
import NotificationItem from "./NotificationItem";
import { KATEGORIA_AKCEPTACJE, KODY_AKCEPTACJI } from "../../constants/notificationCategories";
import styles from "./NotificationPanel.module.css";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/* [AKCEPTACJE 2026-08-29] „Coś czeka na moją decyzję" musi dać się znaleźć spod DZWONKA, bez
   wchodzenia do centrum powiadomień. Rozpoznajemy po KATEGORII z bazy — lista kodów jest tylko
   zabezpieczeniem na środowisko, na którym ujednolicenie kategorii jeszcze się nie przetoczyło. */
const czekaNaDecyzje = (n) =>
    n?.typeCategory === KATEGORIA_AKCEPTACJE || KODY_AKCEPTACJI.includes(n?.typeCode);

const NotificationPanel = () => {
    const navigate = useNavigate();
    const {
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        fetchMore,
        hasMore,
        fetchUnread,
        fetchMoreUnread,
        unreadHasMore,
        closePanel,
        desktopEnabled,
        desktopPermission,
        toggleDesktopNotifications,
    } = useNotifications();

    const [filter, setFilter] = useState("all"); // "all" | "unread"
    const listRef = useRef(null);

    // Filter to last 7 days only
    const recentNotifications = useMemo(() => {
        const cutoff = new Date(Date.now() - SEVEN_DAYS_MS);
        return notifications.filter(n => new Date(n.createdAt) >= cutoff);
    }, [notifications]);

    // Kolejka „do decyzji" celowo NIE jest ograniczona do 7 dni: sprawa czekająca dwa tygodnie
    // jest PILNIEJSZA od świeżej, a odcięcie po dacie ukrywałoby dokładnie te zaległe.
    const doDecyzji = useMemo(
        () => notifications.filter((n) => !n.isRead && czekaNaDecyzje(n)),
        [notifications]
    );

    /* 🔴 [OBSZAR 20, zgłoszenie właściciela 2026-09-18] „w headerze mam licznik ze 5, odczytuję je, a na komputerze
       znowu mam 5."
       CO BYŁO ŹLE: zakładka „Nieprzeczytane" brała `recentNotifications` — czyli 20 najnowszych pozycji przyciętych do
       7 dni — a jej plakietka pokazywała licznik Z SERWERA, liczony po WSZYSTKICH nieprzeczytanych. Nieprzeczytane
       sprzed ponad tygodnia (albo z drugiej strony) były liczone i niewidoczne: nie dało się ich odczytać, bo nie
       dało się ich kliknąć. Do tego dociąganie kolejnych stron działało WYŁĄCZNIE na zakładce „Ostatnie 7 dni".
       TERAZ zakładka „Nieprzeczytane" pokazuje KAŻDĄ nieprzeczytaną pozycję z listy — bez cięcia po dacie — a przy
       wejściu na nią dociąga stronę nieprzeczytanych z serwera (ten sam zbiór, co licznik; patrz NotificationContext).
       Kolejne strony: przewinięcie albo jawny przycisk „Pokaż starsze". */
    const nieprzeczytane = useMemo(() => notifications.filter((n) => !n.isRead), [notifications]);

    useEffect(() => {
        if (filter === "unread" && typeof fetchUnread === "function") fetchUnread(1);
    }, [filter, fetchUnread]);

    const filtered = filter === "unread"
        ? nieprzeczytane
        : filter === "approvals"
            ? doDecyzji
            : recentNotifications;

    /* Ile nieprzeczytanych licznik liczy PONAD to, co widać na zakładce „Ostatnie 7 dni". Dotąd ta różnica była
       niewidoczna — człowiek miał „5" przy dzwonku i pustą listę, i nie miał jak się dowiedzieć, dlaczego. */
    const nieprzeczytaneStarsze = Math.max(0, (unreadCount || 0) - recentNotifications.filter((n) => !n.isRead).length);

    const handleScroll = useCallback(() => {
        const el = listRef.current;
        if (!el) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
            if (hasMore && filter === "all") {
                fetchMore();
            } else if (unreadHasMore && filter === "unread" && typeof fetchMoreUnread === "function") {
                fetchMoreUnread();
            }
        }
    }, [hasMore, filter, fetchMore, unreadHasMore, fetchMoreUnread]);

    /* [OBSZAR B 2026-09-03, poz. 3c] Adres jest już policzony i przetłumaczony w celPowiadomienia()
       (NotificationItem) — tu tylko zamykamy panel i nawigujemy. Tłumaczenie w dwóch miejscach
       naraz było dokładnie tym, przez co dzwonek i centrum potrafiły rozjechać się zachowaniem. */
    const handleNavigate = (adres) => {
        closePanel();
        if (adres) navigate(adres);
    };

    const handleShowAll = () => {
        closePanel();
        navigate(localizedPath("notificationCenter"));
    };

    return (
        <motion.div
            className={styles.panel}
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ duration: 0.18, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
            {/* Header */}
            <div className={styles.header}>
                <h3 className={styles.headerTitle}>Powiadomienia</h3>
                {unreadCount > 0 && (
                    <button className={styles.readAllBtn} onClick={markAllAsRead}>
                        <FiCheck size={14} />
                        <span>Przeczytaj wszystkie</span>
                    </button>
                )}
            </div>

            {/* Tabs */}
            <div className={styles.tabs}>
                <button
                    className={`${styles.tab} ${filter === "all" ? styles.tabActive : ""}`}
                    onClick={() => setFilter("all")}
                >
                    Ostatnie 7 dni
                </button>
                <button
                    className={`${styles.tab} ${filter === "unread" ? styles.tabActive : ""}`}
                    onClick={() => setFilter("unread")}
                >
                    Nieprzeczytane
                    {unreadCount > 0 && <span className={styles.tabBadge}>{unreadCount}</span>}
                </button>
                <button
                    className={`${styles.tab} ${filter === "approvals" ? styles.tabActive : ""}`}
                    onClick={() => setFilter("approvals")}
                    title="Sprawy skierowane do Twojej decyzji"
                >
                    Do decyzji
                    {doDecyzji.length > 0 && <span className={styles.tabBadge}>{doDecyzji.length}</span>}
                </button>
            </div>

            {/* List */}
            <div className={styles.list} ref={listRef} onScroll={handleScroll}>
                {filter === "all" && nieprzeczytaneStarsze > 0 && (
                    <button
                        type="button"
                        className={styles.starszeHint}
                        onClick={() => setFilter("unread")}
                        data-testid="starsze-nieprzeczytane"
                    >
                        Masz jeszcze {nieprzeczytaneStarsze} nieprzeczytan{nieprzeczytaneStarsze === 1 ? "e" : "ych"} starsz{nieprzeczytaneStarsze === 1 ? "e" : "ych"} niż 7 dni — pokaż
                    </button>
                )}
                {filtered.length > 0 ? (
                    <>
                        {filtered.map(notification => (
                            <NotificationItem
                                key={notification.id}
                                notification={notification}
                                onRead={markAsRead}
                                onDelete={deleteNotification}
                                onNavigate={handleNavigate}
                            />
                        ))}
                        {filter === "unread" && unreadHasMore && (
                            <button
                                type="button"
                                className={styles.pokazStarsze}
                                onClick={fetchMoreUnread}
                                data-testid="pokaz-starsze"
                            >
                                Pokaż starsze nieprzeczytane
                            </button>
                        )}
                    </>
                ) : (
                    <div className={styles.empty}>
                        <FiInbox size={32} />
                        <p>Brak powiadomień</p>
                        {/* [OBSZAR B 2026-09-03, poz. 3e] Stan pusty mówi, DLACZEGO jest pusto — inaczej po
                            zawężeniu zakresu (koniec dzwonka o cudzych sprawach) cisza wygląda na awarię. */}
                        <span>
                            {filter === "unread"
                                ? "Wszystko przeczytane — nic nie czeka na Twoją uwagę."
                                : filter === "approvals"
                                    ? "Nic nie czeka na Twoją decyzję."
                                    : "Nic w Twoich sprawach nie wydarzyło się przez ostatnie 7 dni. Dostajesz powiadomienia tylko o tym, co dotyczy Ciebie albo należy do Twoich obowiązków."}
                        </span>
                    </div>
                )}
            </div>

            {/* "Zobacz wszystkie" link */}
            <div className={styles.showAllWrap}>
                <button className={styles.showAllBtn} onClick={handleShowAll}>
                    <span>Zobacz wszystkie powiadomienia</span>
                    <FiArrowRight size={13} />
                </button>
            </div>

            {/* Footer — przełącznik powiadomień systemowych */}
            <div className={styles.footer}>
                <button
                    className={`${styles.desktopToggle} ${desktopEnabled && desktopPermission === "granted" ? styles.desktopToggleOn : ""}`}
                    onClick={toggleDesktopNotifications}
                    title={desktopPermission === "denied"
                        ? "Powiadomienia zablokowane w przeglądarce. Zmień w ustawieniach."
                        : desktopEnabled
                            ? "Wyłącz powiadomienia systemowe"
                            : "Włącz powiadomienia systemowe"
                    }
                >
                    <FiMonitor size={13} />
                    <span>
                        {desktopPermission === "denied"
                            ? "Powiadomienia zablokowane"
                            : desktopEnabled && desktopPermission === "granted"
                                ? "Powiadomienia systemowe aktywne"
                                : "Włącz powiadomienia systemowe"
                        }
                    </span>
                    <div className={`${styles.desktopDot} ${desktopEnabled && desktopPermission === "granted" ? styles.desktopDotOn : ""}`} />
                </button>
            </div>
        </motion.div>
    );
};

export default NotificationPanel;
